import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import SubHeaderComponent from "../../../components/shared/Subheader/subHeader.component";
import {
  StripeProduct,
  PromoCode,
  CouponResponse,
} from "../../../types/stripe";
import {
  getStripeCoupon,
  getStripeProducts,
  getStripePromotionCode,
  renewStripeSubscription,
} from "../../../api/stripeApi";
import LoaderComponent from "../../../components/shared/Loader/Loader.component";
import { getUser, searchUser } from "../../../api/usersApi";
import { Modal } from "react-bootstrap";

const formatMembershipAmount = (amountInCents: number) =>
  `$${(amountInCents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getDiscountedUnitAmount = (
  unitAmount: number,
  coupon: CouponResponse,
) => {
  if (coupon.percent_off) {
    return Math.round(unitAmount * (1 - coupon.percent_off / 100));
  }
  if (coupon.amount_off) {
    return Math.max(unitAmount - Number(coupon.amount_off), 0);
  }
  return unitAmount;
};

const getPlanName = (description: string) => {
  const [name] = description.split(" - ");
  return name || description;
};

const getInstallmentPrefix = (description: string) => {
  const pricePart = description.split(" - ").slice(1).join(" - ");
  const match = pricePart.match(/^(.*)\s\$[\d,]+/);
  return match ? match[1].trim() : "";
};

const RenewMembershipScreen = () => {
  const navigate = useNavigate();
  const { customerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selected, setSelected] = useState("");
  const [productSelected, setProductSelected] = useState("");
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [coupons, setCoupons] = useState<CouponResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPaymentType, setSelectedPaymentType] = useState("one_time_s");
  const [clientSecret, setClientSecret] = useState("");

  const stripe = useStripe();
  const elements = useElements();

  const setError = (error: string) => {
    console.log("error", error);
    Swal.fire({
      icon: "warning",
      title: "Hubo un problema",
      text: error,
    });
  };

  const validateUserData = async () => {
    try {
      if (!customerId) {
        navigate("/login");
        return;
      }
      const search = await searchUser(
        0,
        1,
        "",
        { stripeCustomerId: customerId },
        "",
      );
      if (!search.docs || !search.docs.length) {
        navigate("/login");
        return;
      }
      const user = search.docs[0];
      const userData = await getUser(user._id || "", "");
      if (userData.stripeCustomerId !== customerId) {
        navigate("/login");
        return;
      }
      const { stripeSubscription } = userData;
      if (stripeSubscription) {
        const ableToRenew = stripeSubscription.renewCandidate === true;
        if (!ableToRenew) {
          return Swal.fire({
            icon: "error",
            title: "Error",
            text: "Tu membresía no necesita ser renovada por el momento.",
          }).then(() => {
            navigate("/login");
          });
        }
      }
      await fetchStripeProducts();
    } catch (error) {
      setError((error as any).message);
    }
  };

  const fetchStripeProducts = async () => {
    try {
      const products = await getStripeProducts("");
      if (products) {
        setProducts(products);
        const coupons: { price_id: string; code: string }[] = [];
        products.forEach((product) => {
          product.prices.data.forEach((price) => {
            const { metadata } = price;
            if (metadata) {
              const { renew_coupon } = metadata;
              if (renew_coupon) {
                coupons.push({
                  price_id: price.id,
                  code: renew_coupon as string,
                });
              }
            }
          });
        });
        if (coupons.length > 0) {
          const validatedCoupons: CouponResponse[] = [];
          await Promise.all(
            coupons.map(async (coupon) => {
              try {
                const response = await getStripeCoupon("", coupon.code);
                if (response?.valid) {
                  validatedCoupons.push({
                    ...response,
                    price_id: coupon.price_id,
                  });
                }
              } catch (error) {
                console.log("fetchStripeProducts coupon error", error);
              }
            }),
          );
          setCoupons(validatedCoupons);
        } else {
          setCoupons([]);
        }
        setIsLoading(false);
      }
    } catch (error) {
      setError((error as any).message);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.log("handleSubmit submitError.message", submitError.message);
      setError(
        submitError.message ||
          "Error al procesar el pago. Por favor, inténtalo de nuevo.",
      );
      return;
    }
    const { error } = await stripe.confirmSetup({
      elements,
      clientSecret,
      confirmParams: {
        return_url: "https://app.somaplus.com/login",
      },
      redirect: "if_required",
    });
    if (error) {
      // Show error to your customer (for example, payment details incomplete)
      console.log("handleSubmit stripe.confirmSetup", error);
      setError("Error al procesar el pago. Por favor, inténtalo de nuevo.");
    } else {
      setIsLoading(false);
      setClientSecret("");
      showConfirmation();
    }
  };

  const showConfirmation = () => {
    Swal.fire({
      icon: "success",
      title: "¡Felicidades!",
      text: "Tu suscripción ha sido renovada con éxito.",
      allowEscapeKey: false,
      allowOutsideClick: false,
    }).then(() => {
      navigate("/login");
    });
  };

  const getValidatedRenewCouponId = (priceId: string) => {
    const coupon = coupons.find(
      (item) => item.price_id === priceId && item.valid && Boolean(item.id),
    );
    if (!coupon) {
      return "";
    }
    const restrictedProducts = coupon.applies_to?.products;
    if (
      restrictedProducts &&
      restrictedProducts.length > 0 &&
      !restrictedProducts.includes(productSelected)
    ) {
      return "";
    }
    return coupon.id;
  };

  const handleRenew = async (promoCode: string) => {
    try {
      setIsLoading(true);
      const price = selected;
      if (customerId && price) {
        const couponToApply =
          promoCode !== "" ? promoCode : getValidatedRenewCouponId(price);
        const result = await renewStripeSubscription("", {
          customer: customerId,
          price,
          ...(couponToApply !== "" ? { promoCode: couponToApply } : {}),
          successUrl: `${window.location.origin}/renew-membership/${customerId}?renewed=1`,
          cancelUrl: `${window.location.origin}/renew-membership/${customerId}`,
        });
        if (result?.checkoutUrl) {
          window.location.href = result.checkoutUrl;
          return;
        }
        if (result) {
          const { subscription } = result;
          if (subscription) {
            const { pending_setup_intent } = subscription;
            if (pending_setup_intent) {
              const { status } = pending_setup_intent;
              if (status && status === "requires_payment_method") {
                const { client_secret } = pending_setup_intent;
                setClientSecret(client_secret);
                return;
              }
            }
          }
          showConfirmation();
        }
      }
    } catch (error) {
      setError((error as any).message);
    }
  };

  const handlePromotionCode = async () => {
    try {
      setIsLoading(true);
      Swal.fire({
        title: "Aplica un código promocional",
        input: "text",
        inputLabel: "Ingresa el código promocional",
        inputPlaceholder: "Ingresa el código promocional",
        showCancelButton: true,
        confirmButtonText: "Aplicar y pagar",
        cancelButtonText: "Cancelar",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const { value } = result;
          const response = await getStripePromotionCode("", value);
          if (response) {
            const valid = (response.coupon?.applies_to?.products || []).some(
              (product) => product === productSelected,
            );
            if (valid) {
              handleRenew(response.coupon?.id || "");
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "El código promocional no es válido para este producto.",
              });
            }
          }
          console.log("response", response);
        }
      });
    } catch (error) {
      setError(
        "Error al aplicar el código promocional. Por favor, inténtalo de nuevo.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams.get("renewed") === "1") {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("renewed");
      setSearchParams(nextParams, { replace: true });
      showConfirmation();
      return;
    }
    validateUserData();
  }, []);

  return (
    <>
      <SubHeaderComponent
        href={"/login"}
        buttonText={"Regresar"}
        showBackButton={false}
      />
      <section className="pt-4 mt24 membership-layout">
        <div className="container">
          <div className="row membership--top">
            {clientSecret && (
              <Modal
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                show={clientSecret !== ""}
                className="modal--card"
              >
                <Modal.Header>
                  <Modal.Title id="contained-modal-title-vcenter">
                    <h2 className="h4 mb-0 text-base text-icon">
                      <i className="fa fa-lock" />
                      <span className="ml-2">Confirmación de pago</span>
                    </h2>
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <div className="form-row form--head">
                    <div className="col-12">
                      <p className="text-base">
                        Por favor, confirma tu pago para renovar tu membresía.
                      </p>
                      <div className="form-group">
                        <form id="payment-form" onSubmit={handleSubmit}>
                          <div className="form-row">
                            <PaymentElement />
                          </div>
                          <button
                            type="submit"
                            className="btn btn--type1 w-100"
                            disabled={!stripe}
                          >
                            <span id="button-text">Confirmar pago</span>
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                </Modal.Body>
              </Modal>
            )}
            <div className="membership__col-left col-md-4 col-12">
              <div className="form-row form--head">
                <h1 className="h2 text-gray350 mb-4">
                  Es momento de renovar tu membresia para seguir formando parte
                  de SOMA+.
                </h1>
                <p>
                  Elige la forma de pago y plazo que prefieras, y asegura tu
                  renovación hoy.
                </p>
              </div>
              {isLoading && (
                <div className="mb-4">
                  <LoaderComponent isLoading={isLoading} />
                </div>
              )}
              <div className="form-row tab-change">
                <div
                  className={`form-check form-check-inline ${
                    selectedPaymentType === "one_time_s" ? "active" : ""
                  }`}
                >
                  <input
                    className="form-check-input"
                    type="radio"
                    name="inlineRadioOptions"
                    id="inlineRadio1"
                    value="one_time_s"
                    checked={selectedPaymentType === "one_time_s"}
                    onChange={(e) => setSelectedPaymentType(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="inlineRadio1">
                    Pago único
                  </label>
                </div>
                <div
                  className={`form-check form-check-inline ${
                    selectedPaymentType === "monthly" ? "active" : ""
                  }`}
                >
                  <input
                    className="form-check-input"
                    type="radio"
                    name="inlineRadioOptions"
                    id="inlineRadio2"
                    value="monthly"
                    checked={selectedPaymentType === "monthly"}
                    onChange={(e) => setSelectedPaymentType(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="inlineRadio2">
                    Cuotas
                  </label>
                </div>
              </div>
              {products
                .map((product) =>
                  product.prices.data.filter((price) => {
                    const { metadata } = price;
                    if (metadata) {
                      const { payment_type } = metadata;
                      return payment_type === selectedPaymentType;
                    }
                    return false;
                  }),
                )
                .flat() // Aplanamos el array de arrays
                .sort((a, b) => {
                  const { metadata: metadataA } = a;
                  const { metadata: metadataB } = b;
                  if (metadataA && metadataB) {
                    const { order: orderA } = metadataA;
                    const { order: orderB } = metadataB;
                    if (orderA && orderB) {
                      return orderA - orderB;
                    }
                  }
                  return 0;
                })
                .map((price, index) => {
                  const { metadata, product, unit_amount: unitAmount } = price;
                  const coupon = coupons.find(
                    (coupon) => coupon.price_id === price.id,
                  );
                  const description = `${metadata.Description}`;
                  const discountedAmount = coupon
                    ? getDiscountedUnitAmount(unitAmount, coupon)
                    : unitAmount;
                  const savingsAmount = coupon
                    ? unitAmount - discountedAmount
                    : 0;
                  const installmentPrefix = coupon
                    ? getInstallmentPrefix(description)
                    : "";
                  return (
                    <div className="form-row form--renew" key={index}>
                      <div
                        className={`form-check check--left check--block ${
                          selected === price.id ? "active" : ""
                        } ${coupon ? "has-discount" : ""}`}
                      >
                        <input
                          className="form-check-input"
                          type="radio"
                          name="membership"
                          value="yearly"
                          id={`membership-${index}`}
                          checked={selected === price.id}
                          onChange={() => {
                            setSelected(price.id);
                            setProductSelected(product);
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`membership-${index}`}
                        >
                          {coupon ? (
                            <>
                              <span className="renew-option__title">
                                {getPlanName(description)}
                              </span>
                              {installmentPrefix && (
                                <span className="renew-option__prefix">
                                  {installmentPrefix}
                                </span>
                              )}
                              <span className="renew-option__prices">
                                <span className="renew-option__price-old">
                                  {formatMembershipAmount(unitAmount)}
                                </span>
                                <span className="renew-option__price-new">
                                  {formatMembershipAmount(discountedAmount)}
                                </span>
                              </span>
                              <span className="renew-option__meta">
                                <span className="badge-discount">
                                  {coupon.percent_off
                                    ? `${coupon.percent_off}% de descuento`
                                    : `${coupon.amount_off} de descuento`}
                                </span>
                                {savingsAmount > 0 && (
                                  <span className="renew-option__save">
                                    Ahorras{" "}
                                    {formatMembershipAmount(savingsAmount)}
                                  </span>
                                )}
                              </span>
                            </>
                          ) : (
                            <span>{description}</span>
                          )}
                        </label>
                      </div>
                    </div>
                  );
                })}
            </div>
            <aside className="membership__col-right col-md-8 col-12">
              <figure className="image">
                <img
                  src="/assets/images/elements/frame-membership.png"
                  alt="Membership"
                />
              </figure>
            </aside>
          </div>
          <div className="row membership--bottom">
            <div className="mx-auto mb-3 text-center col-md-8 col-12">
              <button
                type="button"
                className="btn btn--type1 btn--large"
                disabled={selected !== "" ? false : true}
                onClick={(e) => {
                  e.preventDefault();
                  handleRenew("");
                }}
              >
                Pagar
              </button>
            </div>
            {selected !== "" && (
              <div className="mx-auto mb-3 text-center col-md-8 col-12">
                <button
                  type="button"
                  className="btn btn--type2 btn--large"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePromotionCode();
                  }}
                >
                  Tengo un código de descuento
                </button>
              </div>
            )}
            <div className="mx-auto text-center col-md-8 col-12">
              <a href="/login" className="btn btn--link  btn--large">
                Completar más tarde
              </a>
            </div>
            <div className="col-12">
              <ul className="pb-5 list-terms">
                <li>
                  <a href="/terms">Términos de uso</a>
                </li>
                <li>
                  <a href="/privacy">Aviso de privacidad</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default RenewMembershipScreen;
